/**
 * Processing Optimizer Service
 * 
 * Intelligent decision engine for determining optimal processing mode (sync/async)
 * based on real-time performance metrics, system load, and adaptive thresholds.
 * 
 * Features:
 * - Dynamic load assessment and capacity planning
 * - Historical performance pattern analysis
 * - Workload complexity evaluation
 * - Resource availability monitoring
 * - Adaptive threshold adjustment
 * - Circuit breaker pattern for overload protection
 * - Predictive processing mode selection
 */

import { CategoryLogger } from './category-loader';
import { performanceMonitor, PerformanceStats, ResourceMetrics } from './performance-monitor';

/**
 * Processing mode recommendations
 */
export enum ProcessingMode {
  SYNC = 'sync',
  ASYNC = 'async',
  DEFERRED = 'deferred',
  REJECTED = 'rejected'
}

/**
 * Decision factors for processing mode selection
 */
export interface ProcessingDecisionFactors {
  /** Current system load percentage (0-1) */
  systemLoad: number;
  /** Average latency in recent operations */
  averageLatency: number;
  /** Current success rate */
  successRate: number;
  /** Queue depth for async operations */
  queueDepth: number;
  /** Available processing capacity */
  availableCapacity: number;
  /** Estimated complexity of the request */
  requestComplexity: number;
  /** Current error rate */
  errorRate: number;
  /** Resource health score */
  resourceHealth: number;
}

/**
 * Processing recommendation with confidence score
 */
export interface ProcessingRecommendation {
  /** Recommended processing mode */
  mode: ProcessingMode;
  /** Confidence in the recommendation (0-1) */
  confidence: number;
  /** Reasoning for the recommendation */
  reasoning: string;
  /** Estimated processing time */
  estimatedProcessingTime: number;
  /** Alternative modes considered */
  alternatives: Array<{
    mode: ProcessingMode;
    score: number;
    reason: string;
  }>;
  /** Decision factors used */
  factors: ProcessingDecisionFactors;
}

/**
 * Optimizer configuration
 */
export interface OptimizerConfig {
  /** Sync processing timeout threshold (ms) */
  syncTimeoutThreshold: number;
  /** Maximum queue depth before switching to deferred */
  maxQueueDepth: number;
  /** Minimum success rate for sync processing */
  minSyncSuccessRate: number;
  /** Maximum system load for sync processing */
  maxSyncSystemLoad: number;
  /** Circuit breaker error threshold */
  circuitBreakerErrorThreshold: number;
  /** Circuit breaker time window (ms) */
  circuitBreakerTimeWindow: number;
  /** Enable predictive mode selection */
  enablePredictiveMode: boolean;
  /** Learning rate for adaptive thresholds */
  learningRate: number;
}

/**
 * Circuit breaker state
 */
enum CircuitBreakerState {
  CLOSED = 'closed',
  OPEN = 'open',
  HALF_OPEN = 'half_open'
}

/**
 * Request complexity factors
 */
export interface RequestComplexityFactors {
  /** Length of prompt text */
  promptLength: number;
  /** Number of available categories */
  categoryCount: number;
  /** Estimated processing difficulty */
  processingDifficulty: number;
  /** Historical performance for similar requests */
  historicalPerformance?: number;
}

/**
 * Performance history tracking
 */
interface PerformanceHistory {
  timestamp: number;
  mode: ProcessingMode;
  actualProcessingTime: number;
  estimatedProcessingTime: number;
  success: boolean;
  complexity: number;
}

/**
 * Default optimizer configuration
 */
const DEFAULT_CONFIG: OptimizerConfig = {
  syncTimeoutThreshold: 1000,
  maxQueueDepth: 10,
  minSyncSuccessRate: 0.95,
  maxSyncSystemLoad: 0.8,
  circuitBreakerErrorThreshold: 0.2,
  circuitBreakerTimeWindow: 60000, // 1 minute
  enablePredictiveMode: true,
  learningRate: 0.1
};

/**
 * Processing Optimizer Service
 */
export class ProcessingOptimizer {
  private static instance: ProcessingOptimizer;
  private readonly logger: CategoryLogger;
  private readonly config: OptimizerConfig;
  
  // Circuit breaker state
  private circuitBreakerState: CircuitBreakerState = CircuitBreakerState.CLOSED;
  private circuitBreakerLastOpened: number = 0;
  private circuitBreakerFailureCount: number = 0;
  
  // Performance tracking
  private performanceHistory: PerformanceHistory[] = [];
  private readonly maxHistorySize = 1000;
  
  // Adaptive thresholds
  private adaptiveThresholds = {
    syncTimeoutThreshold: DEFAULT_CONFIG.syncTimeoutThreshold,
    maxSystemLoad: DEFAULT_CONFIG.maxSyncSystemLoad,
    minSuccessRate: DEFAULT_CONFIG.minSyncSuccessRate
  };
  
  // Processing mode success tracking
  private modeSuccessRates = new Map<ProcessingMode, { success: number; total: number }>();

  private constructor(config: Partial<OptimizerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = CategoryLogger.getInstance();
    
    this.logger.info('ProcessingOptimizer initialized', 'Constructor', {
      config: this.config,
      adaptiveThresholds: this.adaptiveThresholds
    });
  }

  /**
   * Get singleton instance
   */
  public static getInstance(config?: Partial<OptimizerConfig>): ProcessingOptimizer {
    if (!ProcessingOptimizer.instance) {
      ProcessingOptimizer.instance = new ProcessingOptimizer(config);
    }
    return ProcessingOptimizer.instance;
  }

  /**
   * Get processing recommendation for a classification request
   * @param complexityFactors Request complexity factors
   * @returns Processing recommendation
   */
  public async getProcessingRecommendation(
    complexityFactors: RequestComplexityFactors
  ): Promise<ProcessingRecommendation> {
    const startTime = performance.now();
    
    this.logger.debug('Generating processing recommendation', 'getProcessingRecommendation', {
      complexityFactors
    });

    try {
      // Check circuit breaker state
      if (this.circuitBreakerState === CircuitBreakerState.OPEN) {
        return this.createRejectedRecommendation(
          'Circuit breaker is open - system is overloaded',
          complexityFactors
        );
      }

      // Gather current system metrics
      const decisionFactors = await this.gatherDecisionFactors(complexityFactors);
      
      // Calculate scores for each processing mode
      const modeScores = this.calculateModeScores(decisionFactors, complexityFactors);
      
      // Select best mode based on scores
      const selectedMode = this.selectOptimalMode(modeScores);
      
      // Create recommendation
      const recommendation = this.createRecommendation(
        selectedMode,
        modeScores,
        decisionFactors,
        complexityFactors
      );
      
      const processingTime = performance.now() - startTime;
      
      this.logger.debug('Processing recommendation generated', 'getProcessingRecommendation', {
        recommendedMode: recommendation.mode,
        confidence: recommendation.confidence,
        processingTimeMs: processingTime.toFixed(2),
        systemLoad: decisionFactors.systemLoad,
        queueDepth: decisionFactors.queueDepth
      });

      return recommendation;
    } catch (error) {
      this.logger.error('Failed to generate processing recommendation', 'getProcessingRecommendation', {
        error: error instanceof Error ? error.message : String(error),
        complexityFactors
      }, error instanceof Error ? error : undefined);

      // Fallback to conservative recommendation
      return this.createFallbackRecommendation(complexityFactors);
    }
  }

  /**
   * Record processing result for learning and adaptation
   * @param recommendation Original recommendation
   * @param actualResult Actual processing result
   */
  public recordProcessingResult(
    recommendation: ProcessingRecommendation,
    actualResult: {
      success: boolean;
      actualProcessingTime: number;
      actualMode: ProcessingMode;
    }
  ): void {
    const historyEntry: PerformanceHistory = {
      timestamp: Date.now(),
      mode: actualResult.actualMode,
      actualProcessingTime: actualResult.actualProcessingTime,
      estimatedProcessingTime: recommendation.estimatedProcessingTime,
      success: actualResult.success,
      complexity: recommendation.factors.requestComplexity
    };

    this.performanceHistory.push(historyEntry);
    
    // Trim history if it gets too large
    if (this.performanceHistory.length > this.maxHistorySize) {
      this.performanceHistory = this.performanceHistory.slice(-this.maxHistorySize);
    }

    // Update mode success rates
    this.updateModeSuccessRates(actualResult.actualMode, actualResult.success);
    
    // Update adaptive thresholds based on results
    this.updateAdaptiveThresholds(recommendation, actualResult);
    
    // Update circuit breaker state
    this.updateCircuitBreakerState(actualResult.success);

    this.logger.debug('Recorded processing result', 'recordProcessingResult', {
      recommendedMode: recommendation.mode,
      actualMode: actualResult.actualMode,
      success: actualResult.success,
      actualTime: actualResult.actualProcessingTime,
      estimatedTime: recommendation.estimatedProcessingTime,
      historySize: this.performanceHistory.length
    });
  }

  /**
   * Gather decision factors from current system state
   * @param complexityFactors Request complexity factors
   * @returns Decision factors
   */
  private async gatherDecisionFactors(
    complexityFactors: RequestComplexityFactors
  ): Promise<ProcessingDecisionFactors> {
    // Get performance statistics
    const perfStats = performanceMonitor.getStats(5 * 60 * 1000); // Last 5 minutes
    const resourceMetrics = performanceMonitor.getResourceMetrics();
    const currentResource = resourceMetrics[resourceMetrics.length - 1];

    // Calculate system load
    const systemLoad = this.calculateSystemLoad(perfStats, currentResource);
    
    // Calculate available capacity
    const availableCapacity = this.calculateAvailableCapacity(systemLoad, perfStats);
    
    // Estimate request complexity
    const requestComplexity = this.estimateRequestComplexity(complexityFactors);
    
    // Calculate resource health
    const resourceHealth = this.calculateResourceHealth(currentResource, perfStats);

    return {
      systemLoad,
      averageLatency: perfStats.averageDuration,
      successRate: perfStats.successRate,
      queueDepth: currentResource?.queueDepth || 0,
      availableCapacity,
      requestComplexity,
      errorRate: 1 - perfStats.successRate,
      resourceHealth
    };
  }

  /**
   * Calculate system load based on performance metrics
   * @param perfStats Performance statistics
   * @param resourceMetrics Current resource metrics
   * @returns System load (0-1)
   */
  private calculateSystemLoad(
    perfStats: PerformanceStats,
    resourceMetrics?: ResourceMetrics
  ): number {
    let loadFactors: number[] = [];

    // CPU-like load based on operations per second vs capacity
    const opsPerSecond = perfStats.operationsPerSecond;
    const maxOpsCapacity = 10; // Estimated max operations per second
    const opsLoad = Math.min(opsPerSecond / maxOpsCapacity, 1);
    loadFactors.push(opsLoad);

    // Memory load
    if (resourceMetrics) {
      const memoryLoad = resourceMetrics.memoryUsageMB / 500; // Assume 500MB is high usage
      loadFactors.push(Math.min(memoryLoad, 1));
    }

    // Queue load
    if (resourceMetrics) {
      const queueLoad = resourceMetrics.queueDepth / this.config.maxQueueDepth;
      loadFactors.push(Math.min(queueLoad, 1));
    }

    // Response time load (higher response time = higher load)
    const latencyLoad = Math.min(perfStats.averageDuration / 2000, 1); // 2 seconds is high
    loadFactors.push(latencyLoad);

    // Return weighted average
    return loadFactors.reduce((sum, load) => sum + load, 0) / loadFactors.length;
  }

  /**
   * Calculate available processing capacity
   * @param systemLoad Current system load
   * @param perfStats Performance statistics
   * @returns Available capacity (0-1)
   */
  private calculateAvailableCapacity(
    systemLoad: number,
    perfStats: PerformanceStats
  ): number {
    const baseCapacity = 1 - systemLoad;
    
    // Adjust based on success rate
    const successRateAdjustment = perfStats.successRate;
    
    // Adjust based on recent performance trends
    const trendAdjustment = this.calculatePerformanceTrend();
    
    return Math.max(0, Math.min(1, baseCapacity * successRateAdjustment * trendAdjustment));
  }

  /**
   * Calculate performance trend based on recent history
   * @returns Trend adjustment factor (0.5-1.5)
   */
  private calculatePerformanceTrend(): number {
    if (this.performanceHistory.length < 10) {
      return 1.0; // Neutral trend
    }

    const recentHistory = this.performanceHistory.slice(-10);
    const olderHistory = this.performanceHistory.slice(-20, -10);

    if (olderHistory.length === 0) {
      return 1.0;
    }

    const recentSuccessRate = recentHistory.filter(h => h.success).length / recentHistory.length;
    const olderSuccessRate = olderHistory.filter(h => h.success).length / olderHistory.length;

    const recentAvgTime = recentHistory.reduce((sum, h) => sum + h.actualProcessingTime, 0) / recentHistory.length;
    const olderAvgTime = olderHistory.reduce((sum, h) => sum + h.actualProcessingTime, 0) / olderHistory.length;

    // Calculate trend factors
    const successTrend = recentSuccessRate / olderSuccessRate;
    const timeTrend = olderAvgTime / recentAvgTime; // Inverse - lower time is better

    // Return average trend with bounds
    const averageTrend = (successTrend + timeTrend) / 2;
    return Math.max(0.5, Math.min(1.5, averageTrend));
  }

  /**
   * Estimate request complexity
   * @param complexityFactors Complexity factors
   * @returns Complexity score (0-1)
   */
  private estimateRequestComplexity(complexityFactors: RequestComplexityFactors): number {
    let complexityScore = 0;

    // Prompt length factor (normalized to 0-1)
    const lengthScore = Math.min(complexityFactors.promptLength / 1000, 1) * 0.3;
    complexityScore += lengthScore;

    // Category count factor
    const categoryScore = Math.min(complexityFactors.categoryCount / 20, 1) * 0.2;
    complexityScore += categoryScore;

    // Processing difficulty factor
    const difficultyScore = complexityFactors.processingDifficulty * 0.4;
    complexityScore += difficultyScore;

    // Historical performance factor
    if (complexityFactors.historicalPerformance !== undefined) {
      const historyScore = (1 - complexityFactors.historicalPerformance) * 0.1;
      complexityScore += historyScore;
    }

    return Math.max(0, Math.min(1, complexityScore));
  }

  /**
   * Calculate resource health score
   * @param resourceMetrics Current resource metrics
   * @param perfStats Performance statistics
   * @returns Health score (0-1)
   */
  private calculateResourceHealth(
    resourceMetrics?: ResourceMetrics,
    perfStats?: PerformanceStats
  ): number {
    let healthFactors: number[] = [];

    // Memory health
    if (resourceMetrics) {
      const memoryHealth = 1 - Math.min(resourceMetrics.memoryUsageMB / 400, 1);
      healthFactors.push(memoryHealth);
    }

    // Performance health
    if (perfStats) {
      healthFactors.push(perfStats.successRate);
      
      // Response time health
      const responseTimeHealth = 1 - Math.min(perfStats.averageDuration / 3000, 1);
      healthFactors.push(responseTimeHealth);
    }

    // Circuit breaker health
    const circuitHealth = this.circuitBreakerState === CircuitBreakerState.CLOSED ? 1 : 0;
    healthFactors.push(circuitHealth);

    return healthFactors.length > 0 
      ? healthFactors.reduce((sum, health) => sum + health, 0) / healthFactors.length
      : 0.5;
  }

  /**
   * Calculate scores for each processing mode
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Mode scores
   */
  private calculateModeScores(
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): Map<ProcessingMode, number> {
    const scores = new Map<ProcessingMode, number>();

    // Calculate sync score
    const syncScore = this.calculateSyncScore(factors, complexityFactors);
    scores.set(ProcessingMode.SYNC, syncScore);

    // Calculate async score
    const asyncScore = this.calculateAsyncScore(factors, complexityFactors);
    scores.set(ProcessingMode.ASYNC, asyncScore);

    // Calculate deferred score
    const deferredScore = this.calculateDeferredScore(factors, complexityFactors);
    scores.set(ProcessingMode.DEFERRED, deferredScore);

    // Calculate rejected score (should be low unless system is overloaded)
    const rejectedScore = this.calculateRejectedScore(factors);
    scores.set(ProcessingMode.REJECTED, rejectedScore);

    return scores;
  }

  /**
   * Calculate sync processing score
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Sync score (0-1)
   */
  private calculateSyncScore(
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): number {
    let score = 1.0;

    // Penalize high system load
    score *= (1 - factors.systemLoad * 0.8);

    // Penalize high complexity
    score *= (1 - factors.requestComplexity * 0.6);

    // Penalize low success rate
    score *= factors.successRate;

    // Penalize high queue depth
    const queuePenalty = Math.min(factors.queueDepth / this.config.maxQueueDepth, 1);
    score *= (1 - queuePenalty * 0.5);

    // Penalize high latency
    const latencyPenalty = Math.min(factors.averageLatency / this.adaptiveThresholds.syncTimeoutThreshold, 1);
    score *= (1 - latencyPenalty * 0.7);

    // Bonus for good resource health
    score *= (0.5 + factors.resourceHealth * 0.5);

    // Historical success rate for sync mode
    const syncSuccessRate = this.getModeSuccessRate(ProcessingMode.SYNC);
    score *= (0.7 + syncSuccessRate * 0.3);

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Calculate async processing score
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Async score (0-1)
   */
  private calculateAsyncScore(
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): number {
    let score = 0.8; // Base score for async

    // Prefer async for high complexity
    score += factors.requestComplexity * 0.3;

    // Prefer async for high system load
    score += factors.systemLoad * 0.4;

    // Penalize if queue is too deep
    const queuePenalty = Math.min(factors.queueDepth / (this.config.maxQueueDepth * 2), 1);
    score *= (1 - queuePenalty * 0.8);

    // Bonus for available capacity
    score *= (0.6 + factors.availableCapacity * 0.4);

    // Historical success rate for async mode
    const asyncSuccessRate = this.getModeSuccessRate(ProcessingMode.ASYNC);
    score *= (0.7 + asyncSuccessRate * 0.3);

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Calculate deferred processing score
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Deferred score (0-1)
   */
  private calculateDeferredScore(
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): number {
    let score = 0.3; // Base score for deferred

    // Prefer deferred when system is overloaded
    if (factors.systemLoad > 0.9) {
      score += 0.5;
    }

    // Prefer deferred when queue is deep
    if (factors.queueDepth > this.config.maxQueueDepth) {
      score += 0.4;
    }

    // Prefer deferred when resource health is low
    score += (1 - factors.resourceHealth) * 0.3;

    // Prefer deferred for very high complexity
    if (factors.requestComplexity > 0.8) {
      score += 0.2;
    }

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Calculate rejected processing score
   * @param factors Decision factors
   * @returns Rejected score (0-1)
   */
  private calculateRejectedScore(factors: ProcessingDecisionFactors): number {
    let score = 0.0; // Normally don't reject

    // High score when system is critically overloaded
    if (factors.systemLoad > 0.95) {
      score += 0.8;
    }

    // High score when resource health is critical
    if (factors.resourceHealth < 0.2) {
      score += 0.7;
    }

    // High score when error rate is very high
    if (factors.errorRate > 0.5) {
      score += 0.6;
    }

    // High score when circuit breaker should be open
    if (this.circuitBreakerState === CircuitBreakerState.OPEN) {
      score = 1.0;
    }

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Select optimal processing mode based on scores
   * @param modeScores Map of mode scores
   * @returns Selected processing mode
   */
  private selectOptimalMode(modeScores: Map<ProcessingMode, number>): ProcessingMode {
    let bestMode = ProcessingMode.SYNC;
    let bestScore = 0;

    for (const [mode, score] of modeScores) {
      if (score > bestScore) {
        bestScore = score;
        bestMode = mode;
      }
    }

    // Additional validation based on adaptive thresholds
    if (bestMode === ProcessingMode.SYNC) {
      const syncScore = modeScores.get(ProcessingMode.SYNC) || 0;
      if (syncScore < 0.5) {
        // Sync score too low, fallback to async
        bestMode = ProcessingMode.ASYNC;
      }
    }

    return bestMode;
  }

  /**
   * Create processing recommendation
   * @param selectedMode Selected processing mode
   * @param modeScores All mode scores
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Processing recommendation
   */
  private createRecommendation(
    selectedMode: ProcessingMode,
    modeScores: Map<ProcessingMode, number>,
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): ProcessingRecommendation {
    const selectedScore = modeScores.get(selectedMode) || 0;
    
    // Create alternatives list
    const alternatives = Array.from(modeScores.entries())
      .filter(([mode]) => mode !== selectedMode)
      .map(([mode, score]) => ({
        mode,
        score,
        reason: this.getReasonForMode(mode, score, factors)
      }))
      .sort((a, b) => b.score - a.score);

    // Estimate processing time
    const estimatedTime = this.estimateProcessingTime(selectedMode, factors, complexityFactors);

    // Generate reasoning
    const reasoning = this.generateReasoning(selectedMode, selectedScore, factors, complexityFactors);

    return {
      mode: selectedMode,
      confidence: selectedScore,
      reasoning,
      estimatedProcessingTime: estimatedTime,
      alternatives,
      factors
    };
  }

  /**
   * Generate human-readable reasoning for mode selection
   * @param mode Selected mode
   * @param score Mode score
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Reasoning string
   */
  private generateReasoning(
    mode: ProcessingMode,
    score: number,
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): string {
    const reasons: string[] = [];

    switch (mode) {
      case ProcessingMode.SYNC:
        if (factors.systemLoad < 0.5) reasons.push('low system load');
        if (factors.requestComplexity < 0.5) reasons.push('low request complexity');
        if (factors.successRate > 0.9) reasons.push('high success rate');
        if (factors.resourceHealth > 0.8) reasons.push('excellent resource health');
        if (factors.queueDepth < 3) reasons.push('minimal queue depth');
        break;

      case ProcessingMode.ASYNC:
        if (factors.systemLoad > 0.7) reasons.push('high system load');
        if (factors.requestComplexity > 0.6) reasons.push('high request complexity');
        if (factors.queueDepth < this.config.maxQueueDepth) reasons.push('queue capacity available');
        if (factors.availableCapacity > 0.3) reasons.push('processing capacity available');
        break;

      case ProcessingMode.DEFERRED:
        if (factors.systemLoad > 0.9) reasons.push('system overloaded');
        if (factors.queueDepth > this.config.maxQueueDepth) reasons.push('queue at capacity');
        if (factors.resourceHealth < 0.4) reasons.push('poor resource health');
        break;

      case ProcessingMode.REJECTED:
        if (this.circuitBreakerState === CircuitBreakerState.OPEN) reasons.push('circuit breaker open');
        if (factors.systemLoad > 0.95) reasons.push('critical system overload');
        if (factors.resourceHealth < 0.2) reasons.push('critical resource health');
        break;
    }

    const baseReason = `Selected ${mode} processing`;
    if (reasons.length > 0) {
      return `${baseReason} due to: ${reasons.join(', ')}`;
    } else {
      return `${baseReason} (confidence: ${(score * 100).toFixed(1)}%)`;
    }
  }

  /**
   * Get reason for a specific mode score
   * @param mode Processing mode
   * @param score Mode score
   * @param factors Decision factors
   * @returns Reason string
   */
  private getReasonForMode(
    mode: ProcessingMode,
    score: number,
    factors: ProcessingDecisionFactors
  ): string {
    if (score > 0.8) return 'excellent conditions';
    if (score > 0.6) return 'good conditions';
    if (score > 0.4) return 'acceptable conditions';
    if (score > 0.2) return 'poor conditions';
    return 'unfavorable conditions';
  }

  /**
   * Estimate processing time for a mode
   * @param mode Processing mode
   * @param factors Decision factors
   * @param complexityFactors Request complexity factors
   * @returns Estimated time in milliseconds
   */
  private estimateProcessingTime(
    mode: ProcessingMode,
    factors: ProcessingDecisionFactors,
    complexityFactors: RequestComplexityFactors
  ): number {
    const baseTime = factors.averageLatency || 1000;
    const complexityMultiplier = 1 + factors.requestComplexity;
    const loadMultiplier = 1 + (factors.systemLoad * 0.5);

    let modeMultiplier = 1;
    switch (mode) {
      case ProcessingMode.SYNC:
        modeMultiplier = 1;
        break;
      case ProcessingMode.ASYNC:
        modeMultiplier = 1.2; // Slightly longer due to queuing
        break;
      case ProcessingMode.DEFERRED:
        modeMultiplier = 2; // Much longer due to deferral
        break;
      case ProcessingMode.REJECTED:
        return 0; // No processing time
    }

    return Math.round(baseTime * complexityMultiplier * loadMultiplier * modeMultiplier);
  }

  /**
   * Create fallback recommendation when main logic fails
   * @param complexityFactors Request complexity factors
   * @returns Fallback recommendation
   */
  private createFallbackRecommendation(
    complexityFactors: RequestComplexityFactors
  ): ProcessingRecommendation {
    return {
      mode: ProcessingMode.ASYNC,
      confidence: 0.5,
      reasoning: 'Fallback to async due to optimizer error',
      estimatedProcessingTime: 2000,
      alternatives: [],
      factors: {
        systemLoad: 0.5,
        averageLatency: 1000,
        successRate: 0.8,
        queueDepth: 5,
        availableCapacity: 0.5,
        requestComplexity: this.estimateRequestComplexity(complexityFactors),
        errorRate: 0.2,
        resourceHealth: 0.5
      }
    };
  }

  /**
   * Create rejected recommendation
   * @param reason Rejection reason
   * @param complexityFactors Request complexity factors
   * @returns Rejected recommendation
   */
  private createRejectedRecommendation(
    reason: string,
    complexityFactors: RequestComplexityFactors
  ): ProcessingRecommendation {
    return {
      mode: ProcessingMode.REJECTED,
      confidence: 1.0,
      reasoning: reason,
      estimatedProcessingTime: 0,
      alternatives: [],
      factors: {
        systemLoad: 1.0,
        averageLatency: 0,
        successRate: 0,
        queueDepth: this.config.maxQueueDepth,
        availableCapacity: 0,
        requestComplexity: this.estimateRequestComplexity(complexityFactors),
        errorRate: 1.0,
        resourceHealth: 0
      }
    };
  }

  /**
   * Update mode success rates
   * @param mode Processing mode that was used
   * @param success Whether it was successful
   */
  private updateModeSuccessRates(mode: ProcessingMode, success: boolean): void {
    if (!this.modeSuccessRates.has(mode)) {
      this.modeSuccessRates.set(mode, { success: 0, total: 0 });
    }

    const stats = this.modeSuccessRates.get(mode)!;
    stats.total++;
    if (success) {
      stats.success++;
    }
  }

  /**
   * Get success rate for a processing mode
   * @param mode Processing mode
   * @returns Success rate (0-1)
   */
  private getModeSuccessRate(mode: ProcessingMode): number {
    const stats = this.modeSuccessRates.get(mode);
    if (!stats || stats.total === 0) {
      return 0.8; // Default assumption
    }
    return stats.success / stats.total;
  }

  /**
   * Update adaptive thresholds based on performance results
   * @param recommendation Original recommendation
   * @param result Actual result
   */
  private updateAdaptiveThresholds(
    recommendation: ProcessingRecommendation,
    result: { success: boolean; actualProcessingTime: number; actualMode: ProcessingMode }
  ): void {
    if (!this.config.enablePredictiveMode) {
      return;
    }

    const learningRate = this.config.learningRate;

    // Update sync timeout threshold
    if (result.actualMode === ProcessingMode.SYNC) {
      if (result.success && result.actualProcessingTime < this.adaptiveThresholds.syncTimeoutThreshold) {
        // Success under threshold - slightly decrease threshold for efficiency
        this.adaptiveThresholds.syncTimeoutThreshold *= (1 - learningRate * 0.1);
      } else if (!result.success || result.actualProcessingTime > this.adaptiveThresholds.syncTimeoutThreshold) {
        // Failure or timeout - increase threshold for reliability
        this.adaptiveThresholds.syncTimeoutThreshold *= (1 + learningRate * 0.2);
      }
    }

    // Bound the threshold
    this.adaptiveThresholds.syncTimeoutThreshold = Math.max(500, Math.min(5000, this.adaptiveThresholds.syncTimeoutThreshold));

    this.logger.debug('Updated adaptive thresholds', 'updateAdaptiveThresholds', {
      syncTimeoutThreshold: this.adaptiveThresholds.syncTimeoutThreshold,
      recommendedMode: recommendation.mode,
      actualMode: result.actualMode,
      success: result.success,
      actualTime: result.actualProcessingTime
    });
  }

  /**
   * Update circuit breaker state based on results
   * @param success Whether the operation was successful
   */
  private updateCircuitBreakerState(success: boolean): void {
    const now = Date.now();

    if (this.circuitBreakerState === CircuitBreakerState.CLOSED) {
      if (!success) {
        this.circuitBreakerFailureCount++;
        
        // Check if we should open the circuit breaker
        const recentFailures = this.performanceHistory
          .filter(h => h.timestamp > now - this.config.circuitBreakerTimeWindow)
          .filter(h => !h.success).length;
        
        const recentTotal = this.performanceHistory
          .filter(h => h.timestamp > now - this.config.circuitBreakerTimeWindow).length;

        if (recentTotal > 10 && (recentFailures / recentTotal) > this.config.circuitBreakerErrorThreshold) {
          this.circuitBreakerState = CircuitBreakerState.OPEN;
          this.circuitBreakerLastOpened = now;
          
          this.logger.warn('Circuit breaker opened due to high failure rate', 'updateCircuitBreakerState', {
            failureRate: (recentFailures / recentTotal).toFixed(3),
            threshold: this.config.circuitBreakerErrorThreshold,
            recentFailures,
            recentTotal
          });
        }
      } else {
        this.circuitBreakerFailureCount = Math.max(0, this.circuitBreakerFailureCount - 1);
      }
    } else if (this.circuitBreakerState === CircuitBreakerState.OPEN) {
      // Check if we should try half-open
      if (now - this.circuitBreakerLastOpened > this.config.circuitBreakerTimeWindow) {
        this.circuitBreakerState = CircuitBreakerState.HALF_OPEN;
        this.logger.info('Circuit breaker moved to half-open state', 'updateCircuitBreakerState');
      }
    } else if (this.circuitBreakerState === CircuitBreakerState.HALF_OPEN) {
      if (success) {
        this.circuitBreakerState = CircuitBreakerState.CLOSED;
        this.circuitBreakerFailureCount = 0;
        this.logger.info('Circuit breaker closed after successful operation', 'updateCircuitBreakerState');
      } else {
        this.circuitBreakerState = CircuitBreakerState.OPEN;
        this.circuitBreakerLastOpened = now;
        this.logger.warn('Circuit breaker reopened after failed operation', 'updateCircuitBreakerState');
      }
    }
  }

     /**
    * Get current optimizer status and metrics
    * @returns Optimizer status
    */
   public getOptimizerStatus(): {
     circuitBreakerState: CircuitBreakerState;
     adaptiveThresholds: {
       syncTimeoutThreshold: number;
       maxSystemLoad: number;
       minSuccessRate: number;
     };
     modeSuccessRates: Map<ProcessingMode, { success: number; total: number }>;
     performanceHistorySize: number;
   } {
     return {
       circuitBreakerState: this.circuitBreakerState,
       adaptiveThresholds: { ...this.adaptiveThresholds },
       modeSuccessRates: new Map(this.modeSuccessRates),
       performanceHistorySize: this.performanceHistory.length
     };
   }

  /**
   * Reset optimizer state (for testing or recovery)
   */
  public reset(): void {
    this.circuitBreakerState = CircuitBreakerState.CLOSED;
    this.circuitBreakerLastOpened = 0;
    this.circuitBreakerFailureCount = 0;
    this.performanceHistory = [];
    this.modeSuccessRates.clear();
    this.adaptiveThresholds = {
      syncTimeoutThreshold: DEFAULT_CONFIG.syncTimeoutThreshold,
      maxSystemLoad: DEFAULT_CONFIG.maxSyncSystemLoad,
      minSuccessRate: DEFAULT_CONFIG.minSyncSuccessRate
    };

    this.logger.info('ProcessingOptimizer reset', 'reset');
  }

  /**
   * Temporarily adjust rejection thresholds for debugging (use with caution)
   * This is a temporary workaround while investigating root causes
   */
  public adjustRejectionThresholds(options: {
    systemLoadThreshold?: number;
    resourceHealthThreshold?: number;
    errorRateThreshold?: number;
    temporaryDisable?: boolean;
  }): void {
    this.logger.warn('Adjusting rejection thresholds - temporary debugging measure', 'adjustRejectionThresholds', {
      options,
      originalThresholds: {
        systemLoad: 0.95,
        resourceHealth: 0.2,
        errorRate: 0.5
      }
    });

    // Store original method
    const originalCalculateRejectedScore = this.calculateRejectedScore;

    // Override with adjusted thresholds
    this.calculateRejectedScore = (factors: ProcessingDecisionFactors): number => {
      if (options.temporaryDisable) {
        return 0.0; // Disable rejections entirely
      }

      let score = 0.0;

      const systemLoadThreshold = options.systemLoadThreshold ?? 0.98; // More lenient
      const resourceHealthThreshold = options.resourceHealthThreshold ?? 0.1; // More lenient
      const errorRateThreshold = options.errorRateThreshold ?? 0.7; // More lenient

      if (factors.systemLoad > systemLoadThreshold) {
        score += 0.8;
      }

      if (factors.resourceHealth < resourceHealthThreshold) {
        score += 0.7;
      }

      if (factors.errorRate > errorRateThreshold) {
        score += 0.6;
      }

      if (this.circuitBreakerState === CircuitBreakerState.OPEN) {
        score = 1.0;
      }

      return Math.max(0, Math.min(1, score));
    };

    this.logger.warn('Rejection thresholds adjusted - remember to restore defaults after debugging', 'adjustRejectionThresholds');
  }

  /**
   * Restore default rejection thresholds
   */
  public restoreDefaultRejectionThresholds(): void {
    this.logger.info('Restoring default rejection thresholds', 'restoreDefaultRejectionThresholds');
    
    // Restore original implementation
    this.calculateRejectedScore = (factors: ProcessingDecisionFactors): number => {
      let score = 0.0;

      if (factors.systemLoad > 0.95) {
        score += 0.8;
      }

      if (factors.resourceHealth < 0.2) {
        score += 0.7;
      }

      if (factors.errorRate > 0.5) {
        score += 0.6;
      }

      if (this.circuitBreakerState === CircuitBreakerState.OPEN) {
        score = 1.0;
      }

      return Math.max(0, Math.min(1, score));
    };
  }
}

// Export singleton instance
export const processingOptimizer = ProcessingOptimizer.getInstance(); 